import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Button
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    ChevronRightIcon,
    HeartIcon,
    PlusIcon
} from "../../anatomy/icons";

const NAME = "Button";

const USAGE_CODE = `const [isSaving, setIsSaving] = useState(false);

<Button
    onPress={() => setIsSaving(true)}
    isLoading={isSaving}
    title="Save"
    icon={PlusIcon}
/>`;

const VARIANTS = [
    "filled",
    "tonal",
    "outline",
    "ghost",
    "link"
] as const;

const TYPES = [
    "primary",
    "neutral",
    "danger",
    "warning",
    "success",
    "info"
] as const;

const SIZES = [
    "xSmall",
    "small",
    "medium",
    "large"
] as const;

const SAVE_DELAY = 1200;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("buttonPage.typesTitle"),
        id: "types"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("buttonPage.iconTitle"),
        id: "icon"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("buttonPage.spreadTitle"),
        id: "spread"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ButtonPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isSaving,
        setIsSaving
    ] = useState(false);

    useEffect(() => {
        if(!isSaving) {
            return;
        }

        const timer = window.setTimeout(() => setIsSaving(false), SAVE_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isSaving
    ]);

    return <Article>
        <ComponentHeader
            lead={t("buttonPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("buttonPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("buttonPage.overviewCaption")}
            >
                <Button
                    onPress={() => setIsSaving(true)}
                    title={t("buttonPage.save")}
                    isLoading={isSaving}
                    icon={PlusIcon}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("buttonPage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((variant) => <Button
                        onPress={() => undefined}
                        variant={variant}
                        title={variant}
                        key={variant}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("buttonPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("buttonPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <Button
                        onPress={() => undefined}
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("buttonPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <Button
                        onPress={() => undefined}
                        icon={PlusIcon}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
            <Preview>
                {
                    SIZES.map((size) => <Button
                        accessibilityLabel={t("buttonPage.add")}
                        onPress={() => undefined}
                        icon={PlusIcon}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("buttonPage.iconTitle")}
            id="icon"
        >
            <Paragraph>
                {t("buttonPage.iconText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("buttonPage.continue")}
                    onPress={() => undefined}
                    icon={ChevronRightIcon}
                    iconDirection="end"
                    variant="tonal"
                />
                <Button
                    accessibilityLabel={t("buttonPage.favourite")}
                    onPress={() => undefined}
                    variant="outline"
                    icon={HeartIcon}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("buttonPage.statesText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("buttonPage.disabled")}
                    onPress={() => undefined}
                    isDisabled={true}
                />
                <Button
                    title={t("buttonPage.loading")}
                    onPress={() => undefined}
                    isLoading={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("buttonPage.spreadTitle")}
            id="spread"
        >
            <Paragraph>
                {t("buttonPage.spreadText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Button
                    title={t("buttonPage.save")}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                />
                <Button
                    title={t("buttonPage.cancel")}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                    variant="outline"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("buttonPage.a11yRole"),
                    t("buttonPage.a11yKeyboard"),
                    t("buttonPage.a11yDisabled"),
                    t("buttonPage.a11yLoading"),
                    t("buttonPage.a11yIconOnly")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("buttonPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("buttonPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "\"filled\" | \"tonal\" | \"outline\" | \"ghost\" | \"link\"",
                        description: t("buttonPage.propVariant"),
                        defaultValue: "\"filled\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("buttonPage.propType"),
                        defaultValue: "\"primary\"",
                        name: "type"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\" | \"large\"",
                        description: t("buttonPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("buttonPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("buttonPage.propIconDirection"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "iconDirection"
                    },
                    {
                        description: t("buttonPage.propIconSize"),
                        name: "iconSize",
                        type: "number"
                    },
                    {
                        description: t("buttonPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("buttonPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("buttonPage.propIsPressScaleEnabled"),
                        name: "isPressScaleEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("buttonPage.propRenderLoading"),
                        type: "() => ReactNode",
                        name: "renderLoading"
                    },
                    {
                        description: t("buttonPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("buttonPage.propIsFixVerticalStretch"),
                        name: "isFixVerticalStretch",
                        type: "boolean"
                    },
                    {
                        description: t("buttonPage.propVerticalLocationForStretchFix"),
                        type: "\"start\" | \"center\" | \"end\"",
                        name: "verticalLocationForStretchFix",
                        defaultValue: "\"start\""
                    },
                    {
                        description: t("buttonPage.propBorderRadius"),
                        name: "borderRadius",
                        type: "number"
                    },
                    {
                        description: t("buttonPage.propIsCustomPadding"),
                        name: "isCustomPadding",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("buttonPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("buttonPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("buttonPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("buttonPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ButtonPage;
